import { motion, useMotionValue, useTransform, animate } from 'framer-motion';
import { useEffect, useRef } from 'react';
import { TrendingUp, CheckCircle2, IterationCcw } from 'lucide-react';
import { getImpactMetrics } from './mockData';

function AnimatedCounter({ value, suffix = '' }: { value: number; suffix?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const motionVal = useMotionValue(0);
  const rounded = useTransform(motionVal, (v) => Math.round(v));

  useEffect(() => {
    const controls = animate(motionVal, value, { duration: 1.8, ease: 'easeOut' });
    const unsub = rounded.on('change', (v) => {
      if (ref.current) ref.current.textContent = `${v}${suffix}`;
    });
    return () => { controls.stop(); unsub(); };
  }, [value, suffix, motionVal, rounded]);

  return <span ref={ref}>0{suffix}</span>;
}

const ImpactSummaryBanner = () => {
  const m = getImpactMetrics();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="relative overflow-hidden rounded-2xl border border-primary/20 bg-gradient-to-r from-primary/5 via-accent/5 to-primary/5 backdrop-blur-xl p-6 md:p-8"
    >
      {/* Glow border effect */}
      <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-primary/10 via-transparent to-primary/10 opacity-50" />

      <div className="relative z-10">
        <h2 className="text-sm font-semibold uppercase tracking-widest text-muted-foreground mb-4">
          Impact Summary
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10">
              <IterationCcw className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl md:text-3xl font-bold text-foreground">
                <AnimatedCounter value={m.papersImproved} />
              </p>
              <p className="text-sm text-muted-foreground">
                Papers durch <span className="font-semibold text-foreground">{m.totalIterations}</span> Iterationszyklen verbessert
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-500/10">
              <TrendingUp className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <p className="text-2xl md:text-3xl font-bold text-foreground">
                +<AnimatedCounter value={m.avgImprovement} suffix="%" />
              </p>
              <p className="text-sm text-muted-foreground">
                Ø Verbesserung des Review-Scores nach v2
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-500/10">
              <CheckCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <p className="text-2xl md:text-3xl font-bold text-foreground">
                <AnimatedCounter value={m.submissionReady} />
              </p>
              <p className="text-sm text-muted-foreground">
                Papers einreichungsreif (Ø Score: {m.avgScore}/10)
              </p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ImpactSummaryBanner;
